<template>
  <v-dialog v-model="dialog" max-width="600" persistent>
    <template v-slot:activator="{ on }">
      <v-btn color="primary" v-on="on">
        <v-icon left>mdi-email-send</v-icon>
        {{ $t('global.adminBilling.sendNotification') }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('global.adminBilling.sendNotification') }}</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model.number="formData.deviceId"
                :label="$t('global.adminBilling.deviceId')"
                :rules="[rules.required]"
                type="number"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="formData.notificationType"
                :label="$t('global.adminBilling.notificationType')"
                :items="notificationTypes"
                :rules="[rules.required]"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" v-if="formData.notificationType === 'PRE_EXPIRY'">
              <v-select
                v-model="formData.daysBeforeExpiry"
                :label="$t('global.adminBilling.daysBeforeExpiry')"
                :items="preExpiryDays"
                :rules="[rules.required]"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" v-if="formData.notificationType === 'BILLING_REMINDER'">
              <v-select
                v-model="formData.reminderNumber"
                :label="$t('global.adminBilling.reminderNumber')"
                :items="reminderNumbers"
                :rules="[rules.required]"
                item-text="text"
                item-value="value"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-alert type="info" dense outlined class="mt-2">
            <div v-if="formData.notificationType === 'PRE_EXPIRY'">
              {{ $t('global.adminBilling.preExpiryInfo') }}
            </div>
            <div v-else-if="formData.notificationType === 'BILLING_REMINDER'">
              {{ $t('global.adminBilling.billingReminderInfo') }}
            </div>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">
          {{ $t('global.cancel') }}
        </v-btn>
        <v-btn color="primary" :loading="loading" :disabled="!valid" @click="send">
          {{ $t('global.send') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ADMIN_SEND_SUBSCRIPTION_NOTIFICATION_MUTATION } from '@/graphql/mutations/adminSendSubscriptionNotification'

export default {
  name: 'SendNotificationModal',

  props: {
    deviceId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      formData: {
        deviceId: null,
        notificationType: 'PRE_EXPIRY',
        daysBeforeExpiry: null,
        reminderNumber: null,
      },
      notificationTypes: [
        { text: 'Pre-Expiry Notification', value: 'PRE_EXPIRY' },
        { text: 'Billing Reminder', value: 'BILLING_REMINDER' },
      ],
      preExpiryDays: [30, 14, 7, 3, 1],
      reminderNumbers: [
        { text: 'Reminder #1 (5 days after expiry)', value: 1 },
        { text: 'Reminder #2 (15 days after expiry)', value: 2 },
        { text: 'Reminder #3 (27 days after expiry)', value: 3 },
        { text: 'Final Reminder #4 (29 days after expiry)', value: 4 },
      ],
      rules: {
        required: (v) => !!v || this.$t('global.required'),
      },
    }
  },

  watch: {
    dialog(val) {
      if (val && this.deviceId) {
        this.formData.deviceId = this.deviceId
      }
    },
  },

  methods: {
    close() {
      this.dialog = false
      this.reset()
    },

    reset() {
      this.formData = {
        deviceId: this.deviceId,
        notificationType: 'PRE_EXPIRY',
        daysBeforeExpiry: null,
        reminderNumber: null,
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    async send() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true
      try {
        const variables = {
          deviceId: this.formData.deviceId,
          notificationType: this.formData.notificationType,
        }

        if (this.formData.notificationType === 'PRE_EXPIRY') {
          variables.daysBeforeExpiry = this.formData.daysBeforeExpiry
        } else if (this.formData.notificationType === 'BILLING_REMINDER') {
          variables.reminderNumber = this.formData.reminderNumber
        }

        await this.$apollo.mutate({
          mutation: ADMIN_SEND_SUBSCRIPTION_NOTIFICATION_MUTATION,
          variables,
        })

        this.$toast.success(this.$t('global.adminBilling.notificationSentSuccess'))
        this.$emit('success')
        this.close()
      } catch (error) {
        console.error('Error sending notification:', error)
        this.$toast.error(error.message || this.$t('global.adminBilling.notificationSendError'))
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
